import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useUpdateDriver, Driver } from '@/hooks/useDrivers';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface EditDriverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driver: Driver;
}

export const EditDriverDialog = ({ open, onOpenChange, driver }: EditDriverDialogProps) => {
  const { t, i18n } = useTranslation();
  const { updateDriver, isUpdating } = useUpdateDriver();

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };
  
  const [firstName, setFirstName] = useState(driver.users?.first_name || '');
  const [lastName, setLastName] = useState(driver.users?.last_name || '');
  const [licenseNumber, setLicenseNumber] = useState(driver.license_number);
  const [licenseExpiry, setLicenseExpiry] = useState<Date | undefined>(
    new Date(driver.license_expiry)
  );
  const [status, setStatus] = useState<'available' | 'on_mission' | 'off_duty'>(driver.status);

  useEffect(() => {
    if (open) {
      setFirstName(driver.users?.first_name || '');
      setLastName(driver.users?.last_name || '');
      setLicenseNumber(driver.license_number);
      setLicenseExpiry(new Date(driver.license_expiry));
      setStatus(driver.status);
    }
  }, [open, driver]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !licenseNumber || !licenseExpiry) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      await updateDriver({
        id: driver.id,
        first_name: firstName,
        last_name: lastName,
        license_number: licenseNumber,
        license_expiry: licenseExpiry.toISOString(),
        status,
      });
      
      toast.success('Chauffeur modifié avec succès');
      onOpenChange(false);
    } catch (error) {
      toast.error('Erreur lors de la modification du chauffeur');
    }
  };

  const driverName = driver.users?.first_name && driver.users?.last_name 
    ? `${driver.users.first_name} ${driver.users.last_name}`
    : driver.users?.first_name || driver.users?.last_name || 'Chauffeur';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
            {t('fleet.dialogs.editDriver')} {driverName}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Prénom */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.firstName')} {t('fleet.dialogs.required')}
            </Label>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Jean"
              className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans']"
            />
          </div>

          {/* Nom */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.lastName')} {t('fleet.dialogs.required')}
            </Label>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Dupont"
              className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans']"
            />
          </div>

          {/* Numéro de permis */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('fleet.dialogs.licenseNumber')} {t('fleet.dialogs.required')}
              </Label>
              {driver.license_verified_at && (
                <span className="text-[10px] text-[#14A38B] font-medium">
                  🔒 {t('fleet.documentStatus.lockedField')}
                </span>
              )}
            </div>
            <Input
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              placeholder="123456789"
              readOnly={!!driver.license_verified_at}
              disabled={!!driver.license_verified_at}
              className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20 font-['Funnel_Sans'] disabled:bg-[#EBF4F8] disabled:opacity-100"
            />
          </div>

          {/* Date d'expiration du permis */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
                {t('fleet.dialogs.licenseExpiry')} {t('fleet.dialogs.required')}
              </Label>
              {driver.license_verified_at && (
                <span className="text-[10px] text-[#14A38B] font-medium">
                  🔒 {t('fleet.documentStatus.lockedField')}
                </span>
              )}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={!!driver.license_verified_at}
                  className={cn(
                    "w-full justify-start text-left font-normal border-[#DBE2EA] hover:bg-[#EBF4F8] font-['Funnel_Sans'] disabled:bg-[#EBF4F8] disabled:opacity-100",
                    !licenseExpiry && "text-[#87ADD4]"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-[#5A6B7A]" />
                  {licenseExpiry ? format(licenseExpiry, 'dd MMM yyyy', { locale: getDateLocale() }) : t('fleet.dialogs.select')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={licenseExpiry}
                  onSelect={setLicenseExpiry}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Statut */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide font-['Funnel_Sans']">
              {t('fleet.dialogs.status')}
            </Label>
            <Select value={status} onValueChange={(v: 'available' | 'on_mission' | 'off_duty') => setStatus(v)}>
              <SelectTrigger className="border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">{t('fleet.dialogs.statusAvailable')}</SelectItem>
                <SelectItem value="on_mission">{t('fleet.dialogs.statusOnMission')}</SelectItem>
                <SelectItem value="off_duty">{t('fleet.dialogs.statusOffDuty')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#DBE2EA] text-[#5A6B7A] hover:bg-[#EBF4F8] font-['Funnel_Sans']"
            >
              {t('fleet.dialogs.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isUpdating}
              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg transition-colors duration-200"
            >
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('fleet.dialogs.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};